import { Grid, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/organizations_table/useBatchUpdateItems'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import GeneralCreateButton from 'components/ui/general-create-button'
import GeneralSaveButton from 'components/ui/general-save-button'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { useEffect, useMemo, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import { OrganizationTableItem, OrgTableUpdateItem } from 'types'
import { normalizeDate, validDateValue } from '../../../utils/dateUtils'
import { fieldStyles } from '../style'

dayjs.extend(utc)
dayjs.extend(timezone)

type KickoffCallProps = {
  organization?: OrganizationTableItem
  deal?: OrganizationTableItem
  onCreateDoc: (data: Record<string, any>) => void
  expanded?: boolean
  isCreating?: boolean
}

const KickoffCall: React.FC<KickoffCallProps> = ({ organization, deal, onCreateDoc, expanded, isCreating }) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const qc = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { mutateAsync: batchUpdateItems, isPending: isSaving } = useBatchUpdateItems()
  const userTimezone = dayjs.tz.guess()
  const { patchOrganizationFieldsInCache } = useSyncQueryCache()

  const [formData, setFormData] = useState<Record<string, any>>({})

  // Pre-fill from org/deal once
  useEffect(() => {
    if (organization) {
      setFormData(prev => ({
        ...prev,
        website: organization?.website ?? '',
        socialProfiles: organization?.socialProfiles ?? '',
        vertical: organization?.vertical ?? organization?.vertical?.[0] ?? '',
        location: organization?.location ?? '',
        portal: organization?.portal ?? '',
        dependencies: organization?.dependencies ?? '',
        creatives: organization?.creatives ?? '',
        propertyDetails: organization?.propertyDetails ?? '',
        billingDetails: organization?.billingDetails ?? '',
        extraNotes: organization?.extraNotes ?? '',
        websiteReviewNotes: organization?.websiteReviewNotes ?? '',
        launchDate: organization?.launchDate ?? null,
      }))
    }
  }, [organization])

  useEffect(() => {
    if (deal) {
      setFormData(prev => ({
        ...prev,
        strategyLink: deal?.strategyLink ?? '',
      }))
    }
  }, [deal])

  const handoffCallData = [
    { key: 'clientIntro', label: 'Introduce Client', multiline: false, height: 50, disabled: false },
    { key: 'channels', label: 'Channels', multiline: false, height: 50, disabled: false },
    { key: 'website', label: 'Website', multiline: false, height: 50, disabled: false },
    { key: 'portal', label: 'Prospect Portal', multiline: false, height: 50, disabled: false },
    { key: 'dependencies', label: 'Dependencies', multiline: false, height: 50, disabled: false },
    { key: 'vertical', label: 'Vertical', multiline: false, height: 50, disabled: true },
    { key: 'location', label: 'Location', multiline: false, height: 50, disabled: false },
    { key: 'socialProfiles', label: 'Socials', multiline: false, height: 50, disabled: false },
    { key: 'creatives', label: 'Creatives', multiline: false, height: 50, disabled: false },
    { key: 'strategyLink', label: 'Strategy Link', multiline: false, height: 50, disabled: false }, // DEAL field
    { key: 'propertyDetails', label: 'Property Details', multiline: true, height: 100, disabled: false },
    { key: 'billingDetails', label: 'Billing Details', multiline: true, height: 100, disabled: false },
    { key: 'extraNotes', label: 'Additional Notes', multiline: true, height: 100, disabled: false },
    { key: 'websiteReviewNotes', label: 'Website Review Notes', multiline: true, height: 100, disabled: false },
  ] as const

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }))
  }

  // Which fields go to which item
  const DEAL_FIELDS = new Set<string>(['strategyLink'])
  const ORG_FIELDS = new Set<string>([
    'clientIntro',
    'channels',
    'website',
    'portal',
    'dependencies',
    'vertical',
    'location',
    'socialProfiles',
    'creatives',
    'propertyDetails',
    'billingDetails',
    'extraNotes',
    'websiteReviewNotes',
    'launchDate',
  ])

  const buildUpdates = useMemo(() => {
    return (): OrgTableUpdateItem[] => {
      const tableName = 'ORGANIZATIONS_TABLE'
      const updates: OrgTableUpdateItem[] = []

      const push = (target: any, key: string, val: any) => {
        if (!target?.PK || !target?.SK) return
        if (val === undefined || (typeof val === 'string' && val.trim() === '')) return
        updates.push({ tableName, PK: target.PK, SK: target.SK, fieldName: key, newValue: val })
      }

      Object.entries(formData).forEach(([key, rawVal]) => {
        let val = rawVal

        // normalize launchDate to ISO
        if (key === 'launchDate' && rawVal) {
          val = normalizeDate(rawVal, userTimezone) // string ISO
        }

        // If org originally had array socialProfiles but UI shows text, you could split:
        // if (key === 'socialProfiles' && Array.isArray(organization?.socialProfiles) && typeof rawVal === 'string') {
        //   val = rawVal.split(',').map(s => s.trim()).filter(Boolean)
        // }

        if (DEAL_FIELDS.has(key)) {
          push(deal, key, val)
        } else if (ORG_FIELDS.has(key)) {
          push(organization, key, val)
        }
      })

      return updates
    }
  }, [formData, organization, deal, userTimezone])

  const handleSave = async () => {
    const updates = buildUpdates()
    if (!updates.length) return

    await batchUpdateItems(updates) // <-- pass the array, NOT { updates }
    showSnackbar('Kickoff fields saved', 'success')
  }
  const expanded_height = expanded ? 'calc(100vh - 215px)' : 'calc(100vh - 70px)'
  const pinned_height = `calc(${expanded_height} - 50px)`
  const calc_height = pinned ? pinned_height : expanded_height
  return (
    <BoxScroller sx={{ height: '100%', maxHeight: calc_height, overflowY: 'auto' }}>
      <Grid container spacing={2} mt={2} px={1}>
        <Grid item xs={6}>
          <Typography variant="h5">Kickoff Call</Typography>
        </Grid>

        <Grid item xs={6} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {/* <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
            </Button> */}
          <Tooltip placement="top" title={<Typography>{'Save Doc Info'}</Typography>}>
            <div>
              <GeneralSaveButton onClick={handleSave} disabled={isSaving || isCreating} customWidth={'30'} />
            </div>
          </Tooltip>
          <GeneralCreateButton
            onClick={() => onCreateDoc(formData)}
            disabled={isSaving || isCreating}
            text={'Create Doc'}
            isCreating={isCreating}
            customWidth={'30'}
            placement="top"
          />
        </Grid>

        {handoffCallData.map(field => {
          const raw = formData[field.key] ?? organization?.[field.key] ?? ''
          const value = Array.isArray(raw) ? raw.join(', ') : raw ?? '' // ensure string for TextField

          return !field.multiline ? (
            <Grid item xs={12} sm={6} key={field.key}>
              <TextField
                type="text"
                label={field.label}
                fullWidth
                variant="outlined"
                value={value}
                onChange={e => handleChange(field.key, e.target.value)}
                sx={{ ...fieldStyles(theme.palette.background.input), height: field.height ?? 50 }}
                multiline={false}
                disabled={field.disabled}
                InputProps={{ style: { height: `${field.height}px` } }}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} key={field.key}>
              <TextField
                type="text"
                label={field.label}
                fullWidth
                variant="outlined"
                value={value}
                onChange={e => handleChange(field.key, e.target.value)}
                sx={{ ...fieldStyles(theme.palette.background.input), height: field.height ?? 50 }}
                multiline
                rows={3}
                margin="dense"
                disabled={field.disabled}
                InputProps={{ style: { height: `${field.height}px` } }}
              />
            </Grid>
          )
        })}

        {/* Launch Date picker (org) */}
        <Grid item xs={12} sm={6} key="launch-date-picker-key">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Launch Date"
              value={
                formData.launchDate
                  ? validDateValue(formData.launchDate)
                  : organization?.launchDate
                  ? validDateValue(organization.launchDate)
                  : null
              }
              onChange={newValue => handleChange('launchDate', normalizeDate(newValue, userTimezone))}
              sx={{ ...fieldStyles(theme.palette.background.input), height: 50 }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </BoxScroller>
  )
}

export default KickoffCall
